import { z } from 'zod';
import { DisputeReasonSchema, EvidenceTypeSchema } from '../catalogs/domain-catalogs.js';
import { BusinessDateSchema, TimestampSchema } from '../common/clock.js';
import { AgentSessionIdSchema, ApplicationIdSchema, CreditLineIdSchema, CreditPaymentIdSchema, CustomerIdSchema, DisputeIdSchema, DocumentIdSchema, DrawdownIdSchema, HumanTaskIdSchema, PaymentIntentIdSchema, ReconciliationExceptionIdSchema, ReconciliationRunIdSchema, StatementIdSchema, } from '../common/ids.js';
import { MoneySchema } from '../common/money.js';
import { GuardActionSchema, GuardNameSchema, HumanTaskPrioritySchema, HumanTaskTypeSchema, } from '../agents/states.js';
import { BlockReasonSchema, CreditLineStatusSchema, DelinquencyBucketSchema, } from '../credit/states.js';
import { PaymentAllocationSchema } from '../credit/schemas.js';
import { ApplicationPhaseSchema, CreditDecisionOutcomeSchema, EvidenceDocumentStatusSchema, RecommendationSchema, } from '../origination/states.js';
import { PaymentChannelSchema } from '../payments/schemas.js';
import { DisputeOutcomeSchema, ExceptionSeveritySchema, ReconciliationExceptionTypeSchema, SagaStepSchema, } from '../payments/states.js';
import { ScaMethodSchema } from '../identity/auth.js';
import { ReasonCodeSchema } from '../reason-codes.js';
const ev = (subjectType, producer, webhook, data, version = 1) => ({
    version,
    subjectType,
    producer,
    webhook,
    data,
});
const PayeeSummary = z.object({ name: z.string(), aliasType: z.string() });
export const EVENT_CATALOG = {
    // ── payments ────────────────────────────────────────────────────────────
    'payment.intent.created': ev('payment_intent', 'payments', true, z.object({
        paymentIntentId: PaymentIntentIdSchema,
        customerId: CustomerIdSchema,
        lineId: CreditLineIdSchema,
        amount: MoneySchema,
        installments: z.int(),
        payee: PayeeSummary,
        channel: PaymentChannelSchema,
    })),
    'payment.intent.confirmed': ev('payment_intent', 'payments', true, z.object({ paymentIntentId: PaymentIntentIdSchema, scaMethod: ScaMethodSchema })),
    'payment.intent.sent': ev('payment_intent', 'payments', false, z.object({ paymentIntentId: PaymentIntentIdSchema, endToEndId: z.string() })),
    'payment.intent.pending_confirmation': ev('payment_intent', 'payments', true, z.object({
        paymentIntentId: PaymentIntentIdSchema,
        since: TimestampSchema,
        nextCheckAt: TimestampSchema,
    })),
    'payment.intent.completed': ev('payment_intent', 'payments', true, z.object({
        paymentIntentId: PaymentIntentIdSchema,
        amount: MoneySchema,
        installments: z.int(),
        authCode: z.string(),
        endToEndId: z.string(),
        drawdownId: DrawdownIdSchema.nullable(),
        completedAt: TimestampSchema,
    })),
    'payment.intent.failed': ev('payment_intent', 'payments', true, z.object({
        paymentIntentId: PaymentIntentIdSchema,
        reasonCode: ReasonCodeSchema,
        failedStep: SagaStepSchema.nullable(),
        compensated: z.boolean(),
    })),
    'payment.intent.cancelled': ev('payment_intent', 'payments', true, z.object({ paymentIntentId: PaymentIntentIdSchema })),
    'payment.intent.expired': ev('payment_intent', 'payments', false, z.object({ paymentIntentId: PaymentIntentIdSchema })),
    'payment.refund.received': ev('incoming_refund', 'payments', true, z.object({
        incomingTransferId: z.string(),
        endToEndId: z.string(),
        amount: MoneySchema,
        originalEndToEndId: z.string().nullable(),
        matched: z.boolean(),
    })),
    'payment.refund.applied': ev('incoming_refund', 'payments', true, z.object({
        paymentIntentId: PaymentIntentIdSchema,
        drawdownId: DrawdownIdSchema,
        amount: MoneySchema,
        allocation: PaymentAllocationSchema,
    })),
    'payment.dispute.opened': ev('dispute', 'payments', true, z.object({
        disputeId: DisputeIdSchema,
        paymentIntentId: PaymentIntentIdSchema,
        reason: DisputeReasonSchema,
        amount: MoneySchema,
    })),
    'payment.dispute.decided': ev('dispute', 'payments', true, z.object({
        disputeId: DisputeIdSchema,
        outcome: DisputeOutcomeSchema,
        amount: MoneySchema.nullable(),
        reversalId: z.string().nullable(),
    })),
    'payment.dispute.closed': ev('dispute', 'payments', true, z.object({ disputeId: DisputeIdSchema })),
    'reconciliation.exception.created': ev('reconciliation_exception', 'payments', true, z.object({
        exceptionId: ReconciliationExceptionIdSchema,
        type: ReconciliationExceptionTypeSchema,
        severity: ExceptionSeveritySchema,
        paymentIntentId: PaymentIntentIdSchema.nullable(),
    })),
    'reconciliation.run.completed': ev('reconciliation_run', 'payments', true, z.object({
        runId: ReconciliationRunIdSchema,
        businessDate: BusinessDateSchema,
        exceptions: z.int(),
    })),
    // ── credit ──────────────────────────────────────────────────────────────
    'credit.line.opened': ev('credit_line', 'integration', true, z.object({
        lineId: CreditLineIdSchema,
        customerId: CustomerIdSchema,
        approvedLimit: MoneySchema,
        productId: z.string(),
    })),
    'credit.line.status_changed': ev('credit_line', 'integration', true, z.object({
        lineId: CreditLineIdSchema,
        from: CreditLineStatusSchema,
        to: CreditLineStatusSchema,
        reason: BlockReasonSchema.nullable(),
    })),
    'credit.line.blocked': ev('credit_line', 'integration', true, z.object({ lineId: CreditLineIdSchema, reason: BlockReasonSchema })),
    'credit.line.unblocked': ev('credit_line', 'integration', true, z.object({ lineId: CreditLineIdSchema, reason: BlockReasonSchema })),
    'credit.line.limit_changed': ev('credit_line', 'backoffice', true, z.object({
        lineId: CreditLineIdSchema,
        oldLimit: MoneySchema,
        newLimit: MoneySchema,
        reason: z.string(),
    })),
    'credit.line.delinquency_changed': ev('credit_line', 'integration', true, z.object({
        lineId: CreditLineIdSchema,
        dpd: z.int(),
        bucket: DelinquencyBucketSchema,
    })),
    'credit.statement.generated': ev('statement', 'integration', true, z.object({
        lineId: CreditLineIdSchema,
        statementId: StatementIdSchema,
        cutoffDate: BusinessDateSchema,
        dueDate: BusinessDateSchema,
        minimumPayment: MoneySchema,
        payoffAmount: MoneySchema,
    })),
    'credit.payment.applied': ev('credit_payment', 'integration', true, z.object({
        lineId: CreditLineIdSchema,
        paymentId: CreditPaymentIdSchema,
        amount: MoneySchema,
        allocation: PaymentAllocationSchema,
    })),
    'credit.authorization.expired': ev('credit_authorization', 'integration', false, z.object({
        authorizationId: z.string(),
        lineId: CreditLineIdSchema,
        externalRef: z.string(),
    })),
    // ── origination ─────────────────────────────────────────────────────────
    'origination.application.created': ev('credit_application', 'origination', true, z.object({
        applicationId: ApplicationIdSchema,
        customerId: CustomerIdSchema,
        channel: z.string(),
    })),
    'origination.application.phase_changed': ev('credit_application', 'origination', true, z.object({
        applicationId: ApplicationIdSchema,
        from: ApplicationPhaseSchema,
        to: ApplicationPhaseSchema,
    })),
    'origination.application.submitted': ev('credit_application', 'origination', true, z.object({
        applicationId: ApplicationIdSchema,
        recommendation: RecommendationSchema,
        suggestedLimit: MoneySchema.nullable(),
    })),
    'origination.application.decided': ev('credit_application', 'origination', true, z.object({
        applicationId: ApplicationIdSchema,
        outcome: CreditDecisionOutcomeSchema,
        approvedLimit: MoneySchema.nullable(),
        reasonCodes: z.array(z.string()),
    })),
    'origination.document.uploaded': ev('evidence_document', 'origination', false, z.object({
        documentId: DocumentIdSchema,
        applicationId: ApplicationIdSchema,
        declaredType: EvidenceTypeSchema.nullable(),
    })),
    'origination.document.reviewed': ev('evidence_document', 'agents', true, z.object({
        documentId: DocumentIdSchema,
        detectedType: EvidenceTypeSchema,
        status: EvidenceDocumentStatusSchema,
        issuesCount: z.int(),
    })),
    // ── agents ──────────────────────────────────────────────────────────────
    'agent.human_task.created': ev('human_task', 'agents', true, z.object({
        taskId: HumanTaskIdSchema,
        type: HumanTaskTypeSchema,
        sessionId: AgentSessionIdSchema.nullable(),
        applicationId: ApplicationIdSchema.nullable(),
        priority: HumanTaskPrioritySchema,
    })),
    'agent.session.takeover_changed': ev('agent_session', 'agents', false, z.object({
        sessionId: AgentSessionIdSchema,
        mode: z.enum(['HUMAN', 'AGENT']),
        userId: z.string(),
    })),
    'agent.guardrail.triggered': ev('agent_session', 'agents', false, z.object({
        sessionId: AgentSessionIdSchema,
        guardrail: GuardNameSchema,
        action: GuardActionSchema,
    })),
};
export const EVENT_TYPES = Object.keys(EVENT_CATALOG);
export const EventTypeSchema = z.enum(EVENT_TYPES).meta({ id: 'EventType' });
/** Eventos que la entidad puede suscribir por webhook. */
export const WEBHOOK_EVENT_TYPES = EVENT_TYPES.filter((t) => EVENT_CATALOG[t].webhook);
//# sourceMappingURL=catalog.js.map